// Einstieg: Aufgaben laden, Routing über den URL-Hash (#<aufgaben-id>, leer = Übersicht).

import { api } from "./api.ts";
import type { CountInfo } from "./api.ts";
import { Editor } from "./editor.ts";
import { ICON } from "./icons.ts";
import type { Struktogramm, Task } from "./sim.ts";
import { store } from "./store.ts";

const app = document.getElementById("app")!;
let tasks: Task[] = [];
let editor: Editor | null = null;

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const starStr = (n: number) => "★".repeat(n) + "☆".repeat(3 - n);

function overview(): void {
  const stars = store.stars();
  const total = tasks.reduce((sum, t) => sum + (stars[t.id] ?? 0), 0);
  const max = tasks.length * 3;
  const firstOpen = tasks.findIndex(t => !stars[t.id] && !t.optional); // Vorschlag nie für Zusatzaufgaben

  const card = (t: Task, i: number) => {
    const st = stars[t.id] ? "done" : i === firstOpen ? "next" : "open";
    return `<li><a href="#${t.id}" class="task-card ${st}">
      <span class="num">${st === "done" ? ICON.check : i + 1}</span>
      <span class="title">${esc(t.titel)}
        ${st === "next" ? `<span class="tag">Vorschlag</span>` : ""}
        ${t.optional ? `<span class="tag optional">Optional</span>` : ""}</span>
      <span class="stars" aria-label="${stars[t.id] ?? 0} von 3 Sternen">${starStr(stars[t.id] ?? 0)}</span>
    </a></li>`;
  };

  app.innerHTML = `
    <section class="overview">
      <h1>StructoLab</h1>
      <p class="lead">Bauen Sie Struktogramme (Nassi-Shneiderman) und testen Sie sie Schritt für Schritt -- in beliebiger Reihenfolge.</p>
      <div class="progress" role="img" aria-label="${total} von ${max} Sternen">
        <div class="bar"><div style="width:${max ? (100 * total) / max : 0}%"></div></div>
        <span>${total} / ${max} ★</span>
      </div>
      <ol class="task-list level-path">${tasks.map(card).join("")}</ol>
      <p class="privacy">Ihr Fortschritt wird nur in diesem Browser gespeichert.
        <button type="button" class="link-btn" data-act="reset">Fortschritt löschen</button></p>
      <p class="reset-confirm" hidden>Alle Sterne und gebauten Struktogramme in diesem Browser löschen?
        <button type="button" class="danger" data-act="reset-yes">Ja, löschen</button>
        <button type="button" data-act="reset-no">Abbrechen</button></p>
    </section>`;

  const confirmBox = app.querySelector<HTMLElement>(".reset-confirm")!;
  app.querySelector(".overview")!.addEventListener("click", e => {
    const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
    if (act === "reset") {
      confirmBox.hidden = false;
      confirmBox.querySelector<HTMLElement>("[data-act=reset-no]")!.focus();
    } else if (act === "reset-no") {
      confirmBox.hidden = true;
    } else if (act === "reset-yes") {
      store.clearProgress();
      overview();
    }
  });
}

function route(): void {
  editor?.destroy();
  editor = null;
  const id = decodeURIComponent(location.hash.slice(1));
  const task = tasks.find(t => t.id === id);
  if (task) {
    document.title = `${task.titel} · StructoLab`;
    const i = tasks.indexOf(task);
    const next = tasks[i + 1] ? { id: tasks[i + 1].id, titel: tasks[i + 1].titel } : null;
    editor = new Editor(app, task, next);
    api.open(task.id).then(showBarometer);
  } else {
    document.title = "StructoLab";
    overview();
  }
  window.scrollTo(0, 0);
}

/** „Hörsaal-Barometer“: nur während einer laufenden Vorlesungs-Runde sichtbar. */
function showBarometer(info: CountInfo | null): void {
  const el = app.querySelector<HTMLElement>(".barometer");
  if (!el || !info?.round_active || info.solved_count === null) return;
  const n = info.solved_count;
  el.textContent = n === 0
    ? "Noch niemand im Hörsaal hat diese Aufgabe gelöst -- seien Sie die erste Person!"
    : `Schon ${n} ${n === 1 ? "Person hat" : "Personen haben"} diese Aufgabe im Hörsaal gelöst.`;
  el.hidden = false;
}

app.addEventListener("checked", e => {
  const { program, hints } = (e as CustomEvent<{ program: Struktogramm; hints: number }>).detail;
  const id = decodeURIComponent(location.hash.slice(1));
  api.solve(id, program, hints).then(showBarometer);
});

async function main(): Promise<void> {
  try {
    const res = await fetch("tasks.json", { cache: "no-cache" });
    tasks = (await res.json()).tasks;
  } catch {
    app.innerHTML = `<p class="load-error">Die Aufgaben konnten nicht geladen werden.</p>`;
    return;
  }
  window.addEventListener("hashchange", route);
  route();
}

main();
